import { atom } from "jotai";
import { withImmer } from "jotai-immer";
import { atomWithStorage } from "jotai/utils";
import { id } from "date-fns/locale/id";
import type { Optional } from "utility-types";
import { atomEffect } from "jotai-effect";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { atomWithStore } from "jotai-zustand";

import { addDays, compareDesc, endOfDay, format, formatRelative, isSameMonth, startOfDay, startOfMonth, subDays } from "date-fns";
import { COOKIE_KEYS, setCookie } from "@/helpers/cookie";
import { selectedCategoryOverviewFilterAtom } from "./overview";
import { storage } from "./storage";
export const operatorAtom = atom<string | null>(null);
export const waitingForOperandAtom = atom<boolean>(false);
export const calculatorDisplayValueRefAtom = atom<HTMLElement | undefined>(undefined);

export const openSelectionDateAtom = atom(false);
export const openSelectionCategoryAtom = atom(false);

export type Expense = {
  id: string;
  date: string | "";
  category: string | null;
  amount: number;
  notes: string;
  sync: boolean;
  deleted?: boolean;
};

export type GroupedExpense = {
  id: string;
  date: string;
  category: string | null;
  amount: number;
  notes: string;
  emoji: string;
  color: string;
};

export type ExpenseWithRequiredCategory = Omit<Expense, "category"> & {
  category: NonNullable<Expense["category"]>;
};

const defaultNewExpensePayload = Object.freeze({
  id: undefined,
  date: new Date().toISOString(),
  category: "",
  amount: 0,
  notes: "",
  sync: false,
});

export const primitiveExpensePayloadAtom = atomWithStorage<Optional<Expense, "id">>("new-expense", defaultNewExpensePayload);

export const resetExpensePayloadAtom = atom(null, (_get, set) => {
  set(expensePayloadAtom, defaultNewExpensePayload);
});

export const expensePayloadAtom = withImmer(primitiveExpensePayloadAtom);

export const useExpensesStorage = create<{ expenses: ExpenseWithRequiredCategory[] }>()(
  persist(
    (set, get) => ({
      expenses: [],
    }),
    {
      name: "cashflow",
      storage: createJSONStorage(() => storage),
    },
  ),
);

export const useExpensesPrimitiveStore = atomWithStore(useExpensesStorage);
export const expensesAtom = withImmer(useExpensesPrimitiveStore);

export const cleanedExpensesAtom = atom((get) => {
  const { expenses } = get(expensesAtom) || [];
  return expenses.filter((expense, index, self) => self.findIndex((t) => t?.id === expense?.id) === index);
});

export const expensesUnsyncedAtom = atom((get) => {
  const expenses = get(cleanedExpensesAtom);
  return expenses.filter((expense) => !expense?.sync);
});

export const expensesEffectAtom = atomEffect((get) => {
  const expenses = get(cleanedExpensesAtom).filter((expense) => !expense?.deleted);
  const hasCookie = expenses.length > 0 ? 1 : 0;
  setCookie(COOKIE_KEYS.HAS_EXPENSES, String(hasCookie));
});

export const formatRelativeLocale = {
  lastWeek: "eeee 'lalu'",
  yesterday: "'Kemarin' (dd MMM)",
  today: "'Hari ini' (dd MMM)",
  tomorrow: "'Besok' (dd MMM)",
  nextWeek: "'Besok' eeee",
  other: "dd/MM/yyyy",
};

export const locale = {
  ...id,
  formatRelative: (token: string | number) => formatRelativeLocale[token as keyof typeof formatRelativeLocale],
};

export const expensesGroupedByDateAtom = atom((get) => {
  const expenses = get(cleanedExpensesAtom).filter((expense) => !expense?.deleted);
  const today = new Date();
  const dateKeyMap = new Map();
  const filterCategory = get(selectedCategoryOverviewFilterAtom);
  const { startDate: fromDate, endDate: toDate } = get(chartDateRangeAtom);

  const categories = get(categoriesAtom);
  const grouped = expenses
    .filter(
      (expense) =>
        !expense?.deleted &&
        new Date(expense?.date ?? "") >= fromDate &&
        new Date(expense?.date ?? "") <= toDate &&
        (filterCategory ? expense?.category === filterCategory : true),
    )
    .reduce(
      (acc, expense) => {
        const expenseDate = new Date(expense?.date ?? "");
        const dateKey = formatRelative(expenseDate, today, { locale });

        // Mapping dateKey to actual Date for sorting
        if (!dateKeyMap.has(dateKey)) {
          dateKeyMap.set(dateKey, expenseDate);
        }

        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }

        const category = categories.find((category) => category.value === expense?.category);

        if (!category) {
          acc[dateKey].push({
            ...(expense || {}),
            category: expense?.category + " (Perlu ubah kategori)",
            emoji: "◌",
            color: "rgba(0, 0, 0, 0.1)",
          });

          return acc;
        }

        acc[dateKey].push({
          ...expense,
          category: category.label,
          emoji: category.emoji,
          color: category.color,
        });

        return acc;
      },
      {} as Record<string, GroupedExpense[]>,
    );

  // Sort expenses within each group (based on index)
  Object.keys(grouped).forEach((key) => {
    grouped[key].reverse();
  });

  // Sort the groups based on date
  const sortedGrouped = Object.keys(grouped)
    .sort((a, b) => {
      return compareDesc(dateKeyMap.get(a), dateKeyMap.get(b));
    })
    .reduce(
      (acc, key) => {
        acc[key] = grouped[key];
        return acc;
      },
      {} as Record<string, GroupedExpense[]>,
    );

  return sortedGrouped;
});

export const totalExpensesThisMonthAtom = atom((get) => {
  const expenses = get(cleanedExpensesAtom).filter((expense) => !expense.deleted);
  const today = new Date();
  const { startDate, endDate } = get(chartDateRangeAtom);

  // Check if chartDateRangeAtom is for the current month
  const isCurrentMonth = isSameMonth(startDate, today) && isSameMonth(endDate, today);

  // Use the month from chartDateRangeAtom if it's not the current month
  const targetMonth = isCurrentMonth
    ? new Date(today.getFullYear(), today.getMonth(), 1)
    : new Date(startDate.getFullYear(), startDate.getMonth(), 1);

  const total = expenses.reduce((acc, expense) => {
    const expenseDate = new Date(expense?.date ?? "");
    const isInTargetMonth = isSameMonth(expenseDate, targetMonth);

    if (isInTargetMonth) {
      acc += expense.amount;
    }

    return acc;
  }, 0);

  return total || 0;
});

type ExpensesByWeek = {
  [weekNumber: number]: number;
};

export const averageExpenseThisWeekAtom = atom((get): number => {
  const expenses: Expense[] = get(cleanedExpensesAtom).filter((expense) => !expense.deleted);
  const today = new Date();

  const getWeekNumber = (date: Date): number => {
    const startOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - startOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + startOfYear.getDay() + 1) / 7);
  };

  const currentWeekNumber = getWeekNumber(today);

  const expensesByWeek: ExpensesByWeek = expenses.reduce((acc: ExpensesByWeek, expense: Expense) => {
    const expenseDate = new Date(expense.date);
    const expenseWeekNumber = getWeekNumber(expenseDate);
    if (expenseWeekNumber >= currentWeekNumber - 3 && expenseWeekNumber <= currentWeekNumber) {
      acc[expenseWeekNumber] = (acc[expenseWeekNumber] || 0) + expense.amount;
    }
    return acc;
  }, {});

  const allExpenses = Object.values(expensesByWeek).filter((expense) => typeof expense === "number");
  const averageCurrentWeek = allExpenses.reduce((acc, expense) => acc + expense, 0) / allExpenses.length;

  return averageCurrentWeek || 0;
});

export const expenseCategorySummaryAtom = atom((get) => {
  const expenses = get(cleanedExpensesAtom);
  const today = new Date();
  const startOfCurrentMonth = startOfMonth(today);

  // Filter expenses for the current month
  const currentMonthExpenses = expenses
    .filter((expense) => !expense.deleted)
    .filter((expense) => isSameMonth(new Date(expense.date), startOfCurrentMonth));

  // Calculate total expenses for the month
  const totalExpenses = currentMonthExpenses.reduce((total, expense) => total + expense.amount, 0);

  // Group expenses by category and calculate percentages
  const categoryTotals: Record<string, number> = currentMonthExpenses.reduce(
    (acc, expense) => {
      const { category, amount } = expense;
      if (!acc[category]) {
        acc[category] = 0;
      }
      acc[category] += amount;
      return acc;
    },
    {} as Record<string, number>,
  );

  // Convert to array and calculate percentages
  const categoryPercentages = Object.keys(categoryTotals).map((categoryTotal) => {
    const category = get(categoriesAtom).find((sourceCategory) => sourceCategory.value === categoryTotal);

    return {
      category: category?.label,
      percentage: ((categoryTotals[categoryTotal] / totalExpenses) * 100).toFixed(1),
      emoji: category?.emoji,
      color: category?.color,
    };
  });

  // Sort the categories by percentage in descending order
  categoryPercentages.sort((a, b) => parseFloat(b.percentage) - parseFloat(a.percentage));

  return categoryPercentages;
});

export type DailyExpenses = {
  [date: string]: {
    amount: number;
    dayOfWeek: string;
  };
};

export const chartDateRangeAtom = atom({
  startDate: subDays(startOfDay(new Date()), 6),
  endDate: addDays(endOfDay(new Date()), 2),
});

/**
 * ```js
 * const data = {
    '2023-12-19': { amount: 0, dayOfWeek: 'Sel' }, // Tuesday
    '2023-12-20': { amount: 50, dayOfWeek: 'Rab' }, // Wednesday
    '2023-12-21': { amount: 0, dayOfWeek: 'Kam' }, // Thursday
 }
  ```
 */
export const totalDailyExpensesAtom = atom((get) => {
  const expenses: Expense[] = get(cleanedExpensesAtom).filter((expense) => !expense.deleted);
  const { startDate: fromDate } = get(chartDateRangeAtom);
  let startDate = fromDate ? startOfDay(fromDate) : startOfDay(subDays(new Date(), 6));

  const days = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];
  const dailyExpenses: DailyExpenses = {};

  // Initialize dailyExpenses for the 7-day range
  for (let i = 0; i < 7; i++) {
    const date = addDays(startDate, i);
    const dateString = format(date, "yyyy-MM-dd");
    const dayIndex = parseInt(format(date, "i")) - 1;
    dailyExpenses[dateString] = { amount: 0, dayOfWeek: days[dayIndex] };
  }

  // Aggregate expenses for each day
  expenses.forEach((expense) => {
    const expenseDate = startOfDay(new Date(expense.date));
    const dateString = format(expenseDate, "yyyy-MM-dd");
    if (dailyExpenses[dateString]) {
      dailyExpenses[dateString].amount += expense.amount;
    }
  });

  return dailyExpenses;
});

export const EXPENSE_CATEGORIES = [
  {
    emoji: "🍽️",
    value: "food",
    label: "Food & Dining",
    color: "#ef4444", // red
  },
  {
    emoji: "🛠️",
    value: "utilities",
    label: "Utilities",
    color: "#eab308", // yellow
  },
  {
    emoji: "🚗",
    value: "transportation",
    label: "Transportation",
    color: "#22c55e", // green
  },
  {
    emoji: "🎮",
    value: "entertainment",
    label: "Entertainment",
    color: "#06b6d4", // light blue
  },
  {
    emoji: "🛍️",
    value: "shopping",
    label: "Shopping",
    color: "#3b82f6", // blue
  },
  {
    emoji: "💊",
    value: "healthcare",
    label: "Healthcare",
    color: "#8b5cf6", // purple
  },
];

// Assuming you have a TypeScript type for this
export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];

export const primitiveCategoriesAtom = atomWithStorage<ExpenseCategory[]>("categories", EXPENSE_CATEGORIES);
export const categoriesAtom = withImmer(primitiveCategoriesAtom);
categoriesAtom.debugLabel = "categoriesAtom";

export const categoriesSortedByFrequencyUsageAtom = atom((get) => {
  const expenses = get(cleanedExpensesAtom).filter((expense) => !expense.deleted);
  const categories = get(categoriesAtom);

  // Count the frequency of each category in expenses
  const categoryCounts = expenses.reduce<Record<string, number>>((acc, expense) => {
    acc[expense.category] = (acc[expense.category] || 0) + 1;
    return acc;
  }, {});

  // Sort the categories based on frequency
  const sortedCategories = [...categories].sort((a, b) => {
    const countA = categoryCounts[a.value] || 0;
    const countB = categoryCounts[b.value] || 0;
    return countB - countA;
  });

  return sortedCategories;
});
